import React, { useEffect, useRef, useState } from 'react';
import ActionItems from './ActionItems';
import './ActionItems.css';

const ImpactSection = ({ school }) => {
  const [displayAmount, setDisplayAmount] = useState(0);
  const [voucherType, setVoucherType] = useState('large');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Use the appropriate field based on the selected voucher program size
  const field = voucherType === 'large' ? '5pt' : voucherType === 'medium' ? '3pt' : '1pt';
  const currentTargetAmount = school[field];

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = currentTargetAmount / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= currentTargetAmount) {
        setDisplayAmount(currentTargetAmount);
        clearInterval(timer);
      } else {
        setDisplayAmount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [currentTargetAmount]);

  // Close the dropdown if a click occurs outside the dropdown container.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const selectVoucherType = (selected) => {
    setVoucherType(selected);
    setDropdownOpen(false);
  };

  // Calculate total students and determine the percentage of students that have left
  const totalStudents = school.students;
  const percentDrop = voucherType === 'large' ? 5 : voucherType === 'medium' ? 3 : 1;
  const remainingStudents = totalStudents * ((100 - percentDrop) / 100);

  // The lost teacher count remains based on displayAmount and the fixed divisor.
  const lostTeachers = Math.floor(displayAmount / 53573);

  // Compute the original number of teachers based on the total student population (assuming 15 students per teacher)
  const originalTeachers = Math.ceil(totalStudents / 15);

  // Compute the new number of teachers after the lost teachers
  const newTeachers = originalTeachers - lostTeachers;

  // Now calculate the new ratio using the remaining students (after some have left)
  const newRatio = remainingStudents / newTeachers;

  // The percentage increase is measured relative to the ideal 15:1 ratio.
  const percentageIncrease = ((newRatio - 15) / 15) * 100;

  // Scroll to the representative section
  const scrollToRepresentativeSearch = () => {
    const repSection = document.querySelector('.representative-section');
    console.log('Scrolling to:', repSection);
    if (repSection) {
      repSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="impact-content">
      <h2 className="impact-header">
        If a{' '}
        <span style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
          <span
            className="voucher-dropdown"
            onClick={toggleDropdown}
            style={{ 
              textDecoration: 'underline', 
              textDecorationColor: 'lightgray',
              textDecorationThickness: '2px',
              cursor: 'pointer' 
            }}
          >
            {voucherType}
            <span 
              className="down-caret" 
              style={{ 
                display: 'inline-block',
                fontSize: '0.55em', 
                verticalAlign: 'bottom-text',
                fontFamily: 'Arial Narrow',
                textDecoration: 'none',
                marginBottom: '0px'
              }}
            >
              ⌄
            </span>
          </span>
          {dropdownOpen && (
            <div
              className="autofill-container"
              style={{
                position: 'absolute',
                top: 'calc(100% + 0.3rem)',
                left: 0,
                background: '#333333',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <div
                className="autofill-option"
                onClick={() => selectVoucherType('large')}
                style={{
                  padding: '0.3rem 0.75rem',
                  cursor: 'pointer',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  color: 'var(--text-color)',
                  fontSize: 'clamp(1.25rem, 2vw, 1.5rem)'
                }}
              >
                large
              </div>
              <div
                className="autofill-option"
                onClick={() => selectVoucherType('medium')}
                style={{
                  padding: '0.3rem 0.75rem',
                  cursor: 'pointer',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  color: 'var(--text-color)',
                  fontSize: 'clamp(1.25rem, 2vw, 1.5rem)'
                }}
              >
                medium
              </div>
              <div
                className="autofill-option"
                onClick={() => selectVoucherType('small')}
                style={{
                  padding: '0.3rem 0.75rem',
                  cursor: 'pointer',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                  color: 'var(--text-color)',
                  fontSize: 'clamp(1.25rem, 2vw, 1.5rem)'
                }}
              >
                small
              </div>
            </div>
          )}
        </span>{' '}
        voucher program is implemented in {school.district}, it&nbsp;would&nbsp;drain...
      </h2>
      <div className="impact-number">
        <span>${displayAmount.toLocaleString()}</span>
      </div>
      <p className="impact-subtext">from your neighborhood school district to unaccountable private schools.</p>
      <div className="impact-consequences">
        <h3>That could mean...</h3>
        <ul>
          <li>
            As many as <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>{lostTeachers}</span> fewer teachers for students.
          </li>
          <li>
            <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>{Math.floor(displayAmount / 50).toLocaleString()}</span> fewer library books.
          </li>
          <li>
            A projected increase in student-teacher ratio of{' '}
            <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>{percentageIncrease.toFixed(2)}%</span>.
          </li>
        </ul>
      </div>
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <button className="tel-button" onClick={scrollToRepresentativeSearch}>
          Fight back against vouchers
        </button>
        <ActionItems position="button" />
      </div>
    </div>
  );
};

export default ImpactSection; 